import { Quote, QuoteMood, RequestStatus, Statuses, UserRequest } from 'types/quote';
import { getStatus } from './status';

type RawUserRequest = {
  _id: string;
  project_id: string;
  space_id: string;
  spaceType: 'Room' | 'Apartment';
  createdOn: string;
  updatedOn: string;
  name: string;
  priceTotal?: number;
  priceDetails: UserRequest['priceDetails'];
  checkIn: string;
  checkOut: string;
  pdfPath?: string;
  status: RequestStatus;
  statusReason?: UserRequest['statusReason'];
  mood?: QuoteMood;
  newRequest?: UserRequest['newRequest'];
  oldRequest?: UserRequest['oldRequest'];
  packs?: UserRequest['packs'];
  packsList?: UserRequest['packs'];
  rentPayments?: UserRequest['rentPayments'];
  extraSpaces?: UserRequest['extraSpaces'];
};

/**
 * Normalizes a raw user request returned by the backend into the shared
 * frontend request shape consumed by the dashboard flow.
 */
export const mapUserRawRequest = (rawUserRequest: RawUserRequest, statuses: Statuses): UserRequest => ({
  id: rawUserRequest._id,
  projectId: rawUserRequest.project_id,
  spaceId: rawUserRequest.space_id,
  spaceType: rawUserRequest.spaceType,
  createdOn: rawUserRequest.createdOn,
  updatedOn: rawUserRequest.updatedOn,
  name: rawUserRequest.name,
  // Use priceTotal if available, otherwise fall back to price to avoid undefined/NaN downstream.
  priceTotal: rawUserRequest.priceTotal ?? 0,
  priceDetails: rawUserRequest.priceDetails,
  checkIn: rawUserRequest.checkIn,
  checkOut: rawUserRequest.checkOut,
  pdfPath: rawUserRequest.pdfPath,
  status: getStatus(rawUserRequest.status, statuses),
  statusReason: rawUserRequest.statusReason,
  mood: rawUserRequest.mood,
  newRequest: rawUserRequest.newRequest,
  oldRequest: rawUserRequest.oldRequest,
  packs: rawUserRequest.packs ?? rawUserRequest.packsList,
  rentPayments: rawUserRequest.rentPayments,
  extraSpaces: rawUserRequest.extraSpaces,
});

/**
 * Maps a normalized user request into the quote shape used by the room flow.
 * Room requests intentionally ignore apartment-specific payload such as mood.
 */
export const mapRoomRequestToQuote = (userRequest: UserRequest): Quote => ({
  id: userRequest.id,
  name: userRequest.name,
  price: userRequest.priceTotal,
  dateRange: {
    checkInDate: userRequest.checkIn,
    checkOutDate: userRequest.checkOut,
  },
  createdOn: userRequest.createdOn,
  updatedOn: userRequest.updatedOn,
  priceDetails: userRequest.priceDetails,
  spaceId: userRequest.spaceId,
  pdfPath: userRequest.pdfPath,
  status: userRequest.status,
  statusReason: userRequest.statusReason ?? (userRequest.newRequest?.name ? 'changeSpace' : undefined),
  newRequest: userRequest.newRequest,
  oldRequest: userRequest.oldRequest,
  packs: userRequest.packs,
  rentPayments: userRequest.rentPayments,
  features: userRequest.extraSpaces,
});

/**
 * Maps a normalized user request into the quote shape used by the apartment
 * flow, preserving apartment-specific data needed downstream by the UI.
 */
export const mapApartmentRequestToQuote = (userRequest: UserRequest): Quote => ({
  ...mapRoomRequestToQuote(userRequest),
  mood: userRequest.mood,
});
